import currency from "currency.js"
import { Nullable } from "src/types";
import { customRef, type Ref } from "vue"

/**
 * Creates a debounced version of the provided function that delays its execution
 * until after a specified timeout period has elapsed since it was last invoked.
 *
 * This helps in limiting the rate at which a function can be called, particularly
 * useful for optimizing performance when dealing with events that may fire frequently,
 * like resizing or scrolling.
 *
 * @param fn The function to debounce
 * @param timeout The time in milliseconds to wait before invoking the function. Default is 300ms.
 *
 * @returns a debounced version of the provided function.
 */
export function debounce(fn: (...args: any[]) => void, timeout: number = 300) {
  let timer: any = null
  return (...args: any[]): void => {
    if (timer) return
    timer = setTimeout((): void => {
      fn(...args)
      timer = null
    }, timeout)
  }
}

/**
 * Type guard to check if a value is a Vue ref
 */
function isRef<T>(value: T | Ref<T>): value is Ref<T> {
  return value instanceof Object && "value" in value
}

/**
 * Creates a debounce ref that only updates its value after the specified delay
 * has passed without any new updates.
 *
 * @template T The type of the value being debounce
 * @param value The initial value or a ref containing the value to debounce
 * @param delay The delay in milliseconds before the value updates (default: 200ms)
 * @returns A ref that updates with the debounce value
 *
 * @example
 * ```ts
 * const searchInput = ref('')
 * const debouncedSearch = debounceRef(searchInput, 300)
 *
 * // Alternatively, with a direct value:
 * const debouncedValue = debounceRef('initial value', 300)
 * ```
 */
export function debounceRef<T>(value: T | Ref<T>, delay = 200): Ref<T> {
  let timeout: NodeJS.Timeout | undefined

  return customRef((track, trigger) => ({
    get() {
      track()
      // Use type guard function for better type safety
      return isRef(value) ? value.value : value
    },
    set(newValue: T) {
      if (timeout) {
        clearTimeout(timeout)
      }

      timeout = setTimeout(() => {
        if (isRef(value)) {
          value.value = newValue
        }
        trigger()
      }, delay)
    }
  }))
}

/**
 * Copies the given string value to clipboard.
 *
 * @param value the value to be copied
 */
export async function copyStringToClipboard(value: string): Promise<void> {
  // @ts-ignore
  const clipboardData = window.clipboardData || navigator.clipboard
  await clipboardData.writeText(value)
}

/**
 * Returns either string value, or undefined if it's empty.
 *
 * @param value the string value
 * @returns the string value (or undefined if empty)
 */
export function trimmedOrUndefinedIfEmpty(value?: string | null): string | undefined {
  if (isEmptyStr(value)) {
    return undefined
  }
  return value!.trim()
}

/**
 * Determines if given string value is empty (blank, null, or undefined).
 *
 * @param value the string value to be tested
 * @returns true if empty; otherwise false
 */
export function isEmptyStr(value?: string | null): boolean {
  return (value?.trim() ?? "") == ""
}

/**
 * Capitalizes the first character of the given string.
 *
 * @param value the string value to be capitalized
 *
 * @returns the formatted string
 */
export function capitalizeStr(value: string): string {
  if (value.length > 1) {
    return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase()
  }
  return value.charAt(0).toUpperCase()
}

/**
 * Capitalizes each word in the given string.
 * Example: "hello world" -> "Hello World"
 */
export function capitalizeEachWord(value: string): string {
  return value
    .split(" ")
    .map((word) => capitalizeStr(word))
    .join(" ")
}

/**
 * Formats the given string with underscores to humanize it.
 * Example: "example_string" -> "Example String"
 *
 * @param value the string value to be humanized
 *
 * @returns the formatted string
 */
export function humanizeUnderscoreSeparatedStr(value: string): string {
  return value
    .split("_")
    .map((w) => capitalizeStr(w))
    .join(" ")
}

/**
 * Formats any given string as a FEIN: XX-XXXXXXX.
 *
 * @param value the string value to be formatted
 *
 * @returns the formatted string stripped of non-digits with a hyphen between XX and XXXXXXX
 */
export function formatFEIN(value: string): string {
  value = value.replace(/\D/g, "")
  if (value.length > 2) {
    value = value.replace(/^(\d{2})(\d)/, "$1-$2")
  }
  return value
}

/**
 * Converts a numeric dollar amount into a formatted US currency string.
 * 
 * @param amount - The numeric dollar amount to format (e.g., 1234.56)
 * @param minFractionDigits - The minimum number of fraction digits to display (default: 2)
 * @returns A formatted US currency string (e.g., "$1,234.56")
 * 
 * @example
 * dollarsToFormattedDollarStr(1234.56) // returns "$1,234.56"
 * dollarsToFormattedDollarStr(1234.56, 0) // returns "$1,235"
 */
export function dollarsToFormattedDollarStr(amount: number, minFractionDigits: number = 2): string {
  return amount.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: minFractionDigits,
    maximumFractionDigits: 2,
  })
}

/**
 * Like {@link dollarsToFormattedDollarStr}, but drops the cents fraction when
 * the amount is a whole dollar value. `$225.00` → `$225`; `$1,092.75` stays
 * `$1,092.75`.
 */
export function formatDollarsTrimmed(amount: number): string {
  return dollarsToFormattedDollarStr(amount, Number.isInteger(amount) ? 0 : 2)
}

/**
 * Converts a given US Dollars value to Cents.
 * Example: 24.99 -> 2499
 *
 * @param usd the value in USD
 *
 * @returns the value in Cents
 */
export function usdToCents(usd: number): number {
  return currency(usd, { precision: 2 }).multiply(100).value;
}

/**
 * Converts a given Cents value to US Dollars.
 * Example: 2499 -> 24.99
 *
 * @param cents the value in Cents
 *
 * @returns the value in USD
 */
export function centsToUsd(cents: number): number {
  return currency(cents, { fromCents: true, precision: 2 }).value;
}

/**
 * Copies the given text to clipboard.
 *
 * @param text the text to be copied
 */
export async function copyTextToClipboard(text: string): Promise<void> {
  // @ts-ignore
  const clipboardData = window.clipboardData || navigator.clipboard
  await clipboardData.writeText(text)
}

export function maskSSN(value: string | null | undefined): string {
  if (!value || value.length < 4) {
    return "****"
  }
  return "***-**-" + value.slice(-4)
}

export async function getStringFromStream(stream: ReadableStream, raise: boolean = true): Promise<string> {
  const reader = stream.getReader()
  const decoder = new TextDecoder()
  let result = ""

  try {
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const { done, value } = await reader.read()
      if (done) break
      result += decoder.decode(value, { stream: true })
    }
  } catch (e) {
    if (raise) throw e
  } finally {
    reader.releaseLock()
  }

  return result
}

/**
 * Formats a number as a currency string with configurable decimal places.
 *
 * @param amount - The numeric amount to format, or null
 * @param decimalPlaces - Number of decimal places to display (default: 0)
 * @returns A formatted currency string (e.g., "1,234" or "1,234.56") or "0" if null
 *
 * @example
 * formatCurrency(1234.56) // returns "1,235" (rounded, 0 decimals)
 * formatCurrency(1234.56, 2) // returns "1,234.56" (2 decimal places)
 * formatCurrency(1234.5, 2) // returns "1,234.50" (padded to 2 decimals)
 * formatCurrency(null) // returns "0"
 * formatCurrency(null, 2) // returns "0"
 */
export function formatCurrency(amount: number | null, decimalPlaces: number = 0): string {
  if (amount === null) {
    return "0";
  }
  return amount.toLocaleString("en-US", {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces
  });
}
